import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import { Link } from "react-router-dom";
import { map, get } from "lodash";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";

import UsersAndRoles from "./UsersAndRoles";
import FoldableSection from "../Common/FoldableSection";
import InfoSection from "../Common/InfoSection";
import ScrollableTable from "../Common/Table/ScrollableTable";
import { getAttributeName } from "../../utils/attributes";
import { formatDate } from "../../utils/date";
import colors from "../../assets/style/colors";
import images from "../../assets/img/images";
import { fetchGet } from "../../api/fetch";
import UserModal from "./UserModal";
import { UserStatus } from "../Common/UserStatus";

const itemSizes = {
  xs: 6,
  sm: 3,
  md: 3,
  xl: 3,
};

const columns = [
  {
    name: "users_dateTime",
    minWidth: 150,
  },
  {
    name: "users_action",
    minWidth: 150,
  },
  {
    name: "users_accessType",
    minWidth: 150,
  },
  {
    name: "users_location",
    minWidth: 150,
  },
];

const styles = {
  link: {
    color: colors.primary,
    cursor: "pointer",
    marginLeft: "10px",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
};

const User = (props) => {
  const { classes, language } = props;

  const [user, setUser] = useState();
  const [profile, setProfile] = useState();
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);

  const userId = get(props, ["match", "params", "id4"]);

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchUser = async () => {
    try {
      const userRes = await fetchGet(`users/${userId}`);
      console.log("userRes", userRes);

      const profileRes = await fetchGet(
        `profiles/${get(userRes, ["profile", "id"])}`
      );

      console.log("profileRes", profileRes);
      setUser(userRes);
      setProfile(profileRes);
    } catch (error) {
      console.log(error);
    }
  };

  const onEditUser = () => setIsUserModalOpen(true);

  const handleOpenStatusModal = () => setIsStatusModalOpen(true);

  const handleCloseStatusModal = () => {
    if (!isUpdatingStatus) {
      setIsStatusModalOpen(false);
    }
  };

  const handleConfirmStatusChange = async () => {
    if (!user) return;

    setIsUpdatingStatus(true);
    try {
      const isActive = get(user, ["active"]);

      await fetchGet(`users/${userId}/${isActive ? "disable" : "enable"}`);
      await fetchUser();
    } catch (error) {
      console.log("Error updating user status", error);
    } finally {
      setIsUpdatingStatus(false);
      setIsStatusModalOpen(false);
    }
  };

  const isActive = get(user, ["active"]);
  const statusButtonLabel = isActive
    ? I18n.get("site_disableUser")
    : I18n.get("site_enableUser");
  const statusDialogTitle = statusButtonLabel;
  const statusDialogText = isActive
    ? I18n.get("site_disableUserConfirm")
    : I18n.get("site_enableUserConfirm");
  const buttonsColor = isActive ? "secondary" : "primary";

  const editedUser = {
    id: get(user, ["id"]),
    roles: get(user, ["roles"]) || [],
  };

  return (
    <UsersAndRoles url={get(props, ["match", "url"])} history={props.history}>
      {isUserModalOpen && (
        <UserModal
          isOpen={isUserModalOpen}
          setIsOpen={setIsUserModalOpen}
          editedUser={editedUser}
          onUserUpdated={fetchUser}
          isEditing
        />
      )}
      <Box style={{ padding: "24px", maxWidth: "1300px" }}>
        <Grid container style={{ padding: "0px 0px 16px 8px" }}>
          <Grid
            item
            xs={2}
            className={"bold-500"}
            style={{ textTransform: "uppercase" }}
          >
            <UserStatus active={get(user, ["active"])} />
          </Grid>
          <Grid item>
            <Link
              className={classes.link}
              to={`/profiles/${get(profile, ["id"])}`}
            >
              {I18n.get("users_viewLukeProfile")}
            </Link>
          </Grid>
          <Grid item xs style={{ textAlign: "right" }}>
            <Button
              onClick={onEditUser}
              color="primary"
              startIcon={
                <img
                  src={images.editIconColored}
                  alt="edit"
                  style={{ width: 18, height: 18 }}
                />
              }
            >
              {I18n.get("users_editUserRole")}
            </Button>
            <Button
              style={{ marginLeft: "20px" }}
              color={buttonsColor}
              onClick={handleOpenStatusModal}
            >
              {statusButtonLabel}
            </Button>
            <Dialog
              open={isStatusModalOpen}
              onClose={handleCloseStatusModal}
              fullWidth
              maxWidth="xs"
            >
              <DialogTitle>{statusDialogTitle}</DialogTitle>
              <DialogContent>{statusDialogText}</DialogContent>
              <DialogActions>
                <Button
                  onClick={handleCloseStatusModal}
                  disabled={isUpdatingStatus}
                >
                  {I18n.get("events_no")}
                </Button>
                <Button
                  onClick={handleConfirmStatusChange}
                  color={buttonsColor}
                  disabled={isUpdatingStatus}
                >
                  {isUpdatingStatus
                    ? I18n.get("common_loading") + " ..."
                    : I18n.get("events_yes")}
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>
        </Grid>
        <Grid
          container
          style={{
            paddingBottom: "20px",
            marginBottom: "20px",
            borderBottom: `1px solid ${colors.veryLighPink}`,
          }}
        >
          <Grid item {...itemSizes}>
            <Grid container direction="column">
              <InfoSection
                title2={getAttributeName(31)}
                values={[get(user, ["profile", "firstname"])]}
              />
              <InfoSection
                title2={getAttributeName(32)}
                values={[get(user, ["profile", "lastname"])]}
              />
            </Grid>
          </Grid>
          <Grid item {...itemSizes}>
            <Grid container direction="column">
              <InfoSection
                title={"roles"}
                values={
                  map(get(user, ["roles"]), (r) =>
                    get(r, [`name_${language}`])
                  ) || []
                }
              />
              <InfoSection
                title2={getAttributeName(33)}
                values={[get(user, ["profile", "email"])]}
              />
            </Grid>
          </Grid>
          <Grid item {...itemSizes}>
            <Grid container direction="column">
              <InfoSection
                title="username"
                values={[get(user, ["cognito", "username"]) || ""]}
              />
              <InfoSection
                title={"tag"}
                values={
                  get(profile, ["tag"])
                    ? [get(profile, [`tag_name_${language}`])]
                    : []
                }
                color={
                  get(profile, ["tag"])
                    ? get(profile, ["tag", "color_rgb"])
                    : ""
                }
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid container style={{ marginBottom: "20px" }}>
          <Grid item {...itemSizes}>
            <Grid container>
              <InfoSection
                title2={I18n.get("site_lastModified")}
                values={[formatDate(get(user, ["updated_on"])) || "-"]}
              />
            </Grid>
          </Grid>
          <Grid item {...itemSizes}>
            <Grid container>
              <InfoSection
                title={"createdOn"}
                values={[formatDate(get(user, ["created_on"])) || "-"]}
              />
            </Grid>
          </Grid>
          <Grid item {...itemSizes}>
            <Grid container>
              <InfoSection title={"error"} values={["-"]} />
            </Grid>
          </Grid>
        </Grid>
        <FoldableSection
          title={I18n.get("users_accountActivity")}
          defaultOpen={true}
        >
          <ScrollableTable
            columns={columns}
            fetchData={console.log}
          ></ScrollableTable>
        </FoldableSection>
      </Box>
    </UsersAndRoles>
  );
};

const mapStateToProps = (state) => {
  return {
    language: state.user.language,
  };
};

export default connect(mapStateToProps)(withStyles(styles)(User));
